<template>
  <div class="recipients">
    <TMSHeader
      :title="currentECircular.subject || '--'"
      :buttons="buttons"
      :status="currentECircular.status"
      :dotOption="dotOption"
    />
    <div class="recipients-content">
      <div class="search-arrow-area">
        <TabsContainer
          :key="tabNames.length"
          class="tabs-container-wrapper"
          :tabNames="tabNames"
        >
          <div
            v-if="isMarketingECircular"
            :slot="tabContainersIdMap['marketing']"
            class="search-area_marketing"
          >
            <div class="search-condition">
              <div class="zone medium-6 small-6 large-6 columns">
                <label>{{ $t('tenancy.zone') }}</label>
                <HKLSelect
                  :options="zoneHklOptions"
                  :value="marketingParams.zone || undefined"
                  placeholder=""
                  :showSearch="true"
                  @change="val => updateZone(val, 'marketingParams')"
                />
              </div>
              <div class="marketing-type medium-6 small-6 large-6 columns">
                <label>{{ $t('tenancy.marketingType') }}</label>
                <HKLMultiSelect
                  :options="marketingHklOptions"
                  :value="marketingParams.marketingTypes"
                  placeholder=""
                  class="multi-value"
                  @change="onMarketingTypesChange"
                />
              </div>
              <div class="trade-name medium-12 small-12 large-12 columns">
                <label>{{ $t('eCircular.tradeName') }}</label>
                <div class="search-input">
                  <input
                    class="input-field"
                    :value="marketingParams.tradeName"
                    placeholder=""
                    @change="val => updateTradeName(val, 'marketingParams')"
                  >
                  <button
                    class="btn btn--darkblue"
                    :disabled="isMarketingSearchDisabled"
                    role="search"
                    @click="_searchRecipients(true)"
                  >
                    {{ $t('action.search') }}
                  </button>
                </div>
              </div>
            </div>
            <div class="search-result">
              <SelectRecipients
                ref="select-marketing-recipients"
                operate="ADD"
                :showMarketingType="true"
              />
            </div>
          </div>
          <div
            :slot="tabContainersIdMap['ecircular']"
            class="search-area"
          >
            <div class="search-condition">
              <template v-if="isTenantOrTenancyEcircular">
                <div class="zone medium-6 small-6 large-6 columns">
                  <label>{{ $t('tenancy.zone') }}</label>
                  <HKLSelect
                    :options="zoneHklOptions"
                    :value="pmCircularParams.zone || undefined"
                    placeholder=""
                    :showSearch="true"
                    @change="val => updateZone(val, 'pmCircularParams')"
                  />
                </div>
                <div
                  v-if="isTenantEcircular"
                  class="floor"
                >
                  <label>{{ $t('tenancy.floor') }}</label>
                  <div
                    v-for="(pair, index) in pmCircularParams.floorFromToPair"
                    :key="index"
                    :class="['floorFromToPair', {'error-highlight': pair.err}]"
                  >
                    <HKLSelect
                      :options="floorHklOptions"
                      :value="pair.from || undefined"
                      placeholder=""
                      :showSearch="true"
                      @change="val => updateFloorFromToPair(val, index, 'from')"
                    />
                    <span class="dash">-</span>
                    <HKLSelect
                      :options="floorHklOptions"
                      :value="pair.to || undefined"
                      placeholder=""
                      :showSearch="true"
                      @change="val => updateFloorFromToPair(val, index, 'to')"
                    />
                    <i
                      v-if="pair.to || pair.from || pmCircularParams.floorFromToPair.length > 1"
                      class="fa-times fa"
                      @click="deleteOnePair(index)"
                    />
                  </div>
                  <span class="pair-operate">
                    <i
                      class="fa-plus fa"
                      @click="addOnePair"
                    />
                  </span>
                </div>
                <div class="user-type-field medium-6 small-6 large-6 columns">
                  <label>{{ $t('userType.name') }}</label>
                  <HKLMultiSelect
                    class="multi-value"
                    :options="userTypeOpts"
                    :value="selectedUserContactTypes"
                    placeholder=""
                    :showSearch="true"
                    @change="onUserContactTypesChange"
                  />
                </div>
                <div class="trade-name medium-12 small-12 large-12 columns">
                  <label>{{ $t('eCircular.tradeName') }}</label>
                  <div class="search-input">
                    <input
                      class="input-field"
                      :value="pmCircularParams.tradeName"
                      placeholder=""
                      @change="val => updateTradeName(val, 'pmCircularParams')"
                    >
                    <button
                      class="btn btn--darkblue"
                      :disabled="isPmCircularSearchDisabled"
                      role="search"
                      @click="_searchRecipients(false)"
                    >
                      {{ $t('action.search') }}
                    </button>
                  </div>
                </div>
              </template>
              <template v-if="isVendorEcircular">
                <VendorRecipientsSearch
                  ref="vendor-recipients-search"
                  :initSelectRecipientsTree="initSelectRecipientsTree"
                />
              </template>
            </div>
            <div class="search-result">
              <SelectRecipients
                ref="select-recipients"
                operate="ADD"
                :showMarketingType="false"
              />
            </div>
          </div>
        </TabsContainer>
        <div class="arrow-area">
          <button
            class="btn--darkblue"
            role="add-recipients"
            @click="addToAddedRecipients"
          >
            >>
          </button>
        </div>
      </div>
      <div class="selected-recipients">
        <p class="title">
          {{ $t('eCircular.selectedRecipients') }}
        </p>
        <div class="mount-recipients">
          <span class="item">
            <span class="tip">{{ organizationNumber }}</span>
            <span>{{ $t('eCircular.organizations') }}</span>
          </span>
          <span class="item">
            <span class="tip">{{ recipientsNumber }}</span>
            <span>{{ $t('eCircular.recipientsSelected') }}</span>
          </span>
        </div>
        <SelectRecipients
          ref="delete-recipients"
          operate="DEL"
          :showMarketingType="isMarketingECircular"
        />
      </div>
    </div>
    <PreviewECircular
      v-if="currentECircular.id"
      ref="previewECircular"
      :previewType="PREVIEW_TYPES.SEND"
    />

    <CancelECircular ref="ecircular-cancel-modal" />

    <ActionConfirmModal
      ref="filterRemoveConfirmModal"
      :modalId="filterRemoveConfirmModalId"
      :confirm-title="'eCircular.confirmRemoveUserTypeBody'"
      modal-type="small-modal"
      class="ecircular-modal"
    />
  </div>
</template>

<script>
import { cloneDeep, findIndex, get, isEmpty, isEqual } from 'lodash'
import { mapActions, mapMutations, mapGetters } from 'vuex'
import { computed, ref, watch } from 'vue-demi'
import HKLSelect from '@/controls/Select/HKLSelect'
import HKLMultiSelect from '@/controls/Select/HKLMultiSelect'
import TabsContainer from '@/controls/TabsContainer'
import TMSHeader from '@/controls/TMSHeader'
import mutationTypes from '@/store/mutation-types'
import { aclHelper, aclPermsType, tmsPermsTypes } from '@/utils/aclHelper'
import navigation from '@/utils/navigation'
import i18n from '@/utils/i18n'
import VendorRecipientsSearch from './VendorRecipientsSearch'
import SelectRecipients from './SelectRecipients'
import ActionConfirmModal from '@/controls/ActionConfirmModal'
import CancelECircular from './CancelECircular'
import PreviewECircular from './PreviewECircular'
import {
  PREVIEW_TYPES,
  E_CIRCULAR_TYPES,
  E_CIRCULAR_WORKING_STATUS,
  E_CIRCULAR_EDITABLE_STATUS,
  E_CIRCULAR_USER_TYPE_CODES,
} from './consts'
import {
  pruneAddedRecipientsExclusiveTrCode,
  pruneAddedRecipientsAnyLeafWithTrCode,
  pruneAddedRecipientsExclusiveMarketingRole,
  pruneAddedRecipientsAnyLeafWithMarketingRole,
  buildUserContactTypesFlagsFromSavedUserType,
  buildMarketingTypeLabelsFromSavedUserType,
} from './helper'

export default {
  name: 'AddRecipients',
  components: {
    TMSHeader,
    HKLSelect,
    HKLMultiSelect,
    TabsContainer,
    SelectRecipients,
    ActionConfirmModal,
    CancelECircular,
    PreviewECircular,
    VendorRecipientsSearch,
  },
  setup () {
    const selectedUserContactTypes = ref([])
    const pmCircularParams = ref({
      zone: '',
      floorFromToPair: [],
      tradeName: '',
      userContactTypes: {},
    })
    const userTypeOpts = computed(() => {
      return [
        { name: i18n.t('company.contactTable.ecircular'), value: 'isCircular' },
        { name: i18n.t('company.contactTable.fireMarshal'), value: 'isFireMarshal' },
      ]
    })
    watch(selectedUserContactTypes, (keys) => {
      const flags = {}
      ;(keys || []).forEach((key) => {
        if (key === 'isCircular') flags.isCircular = true
        if (key === 'isFireMarshal') flags.isFireMarshal = true
      })
      pmCircularParams.value.userContactTypes = flags
    })
    return {
      userTypeOpts,
      selectedUserContactTypes,
      pmCircularParams,
    }
  },
  data () {
    return {
      marketingParams: {
        zone: '',
        marketingTypes: [],
        tradeName: '',
      },
      companyName: '',
      PREVIEW_TYPES,
      currentTabIndex: 0,
      marketingOptions: [this.$t('tenancy.roleTypeOptions.marketing'),
        this.$t('tenancy.roleTypeOptions.CRM'),
        this.$t('tenancy.roleTypeOptions.PR'),
        this.$t('tenancy.roleTypeOptions.shop')],
      filterRemoveConfirmModalId: `filterRemoveRecipients-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    }
  },
  computed: {
    ...mapGetters('user', {
      currentUser: 'currentUser',
      zones: 'zones',
    }),
    ...mapGetters('ecircular', ['currentECircular', 'addedRecipients', 'searchRecipientsConditions']),
    buttons () {
      return [{
        title: this.$t('action.save'),
        cls: 'btn--darkblue',
        func: this.save,
      }, {
        title: this.$t('action.preview'),
        cls: 'btn--orange',
        func: this.preview,
      }]
    },
    tabNames () {
      return this.isMarketingECircular ? [this.$t('eCircular.chooseMarketingRecipients'), this.$t(`eCircular.chooseRecipients.${this.currentECircular.type}`)] : [this.$t(`eCircular.chooseRecipients.${this.currentECircular.type}`)]
    },
    tabContainersIdMap () {
      return this.isMarketingECircular ? {
        marketing: 'tab-0',
        ecircular: 'tab-1',
      } : { ecircular: 'tab-0' }
    },
    zoneOptions () {
      return [...this.zones]
    },
    zoneHklOptions () {
      return this.zoneOptions.map((zone) => ({ name: zone, value: zone }))
    },
    marketingHklOptions () {
      return this.marketingOptions.map((label) => ({ name: label, value: label }))
    },
    floorOptions () {
      const filteredBuildings = this.currentUser.buildings.filter(building => building.zones.includes(this.pmCircularParams.zone))
      const selectedZoneBuilding = filteredBuildings.length
        ? filteredBuildings.reduce((maxB, b) => {
          const maxLen = Array.isArray(maxB.floor) ? maxB.floor.length : 0
          const bLen = Array.isArray(b.floor) ? b.floor.length : 0
          return bLen > maxLen ? b : maxB
        }, filteredBuildings[0]) : { floor: [] }
      return selectedZoneBuilding.floor
    },
    floorHklOptions () {
      return (this.floorOptions || []).map((floor) => ({ name: floor, value: floor }))
    },
    organizationNumber () {
      return this.addedRecipients.length
    },
    recipientsNumber () {
      let num = 0
      this.addedRecipients.map(root => {
        num += root.recipients.length
      })
      return num
    },
    canCreateUpdate () {
      return aclHelper.getAccess(tmsPermsTypes.tenantEcircular.createUpdate) || aclHelper.getAccess(aclPermsType.vendorECircular.createUpdateSelectSendCancel)
    },
    canCancel () {
      return (aclHelper.getAccess(tmsPermsTypes.tenantEcircular.cancel) || aclHelper.getAccess(aclPermsType.vendorECircular.createUpdateSelectSendCancel)) && Object.values(E_CIRCULAR_WORKING_STATUS).includes(this.currentECircular.status)
    },
    dotOption () {
      let options = []
      if (this.canCreateUpdate) {
        options.push({
          title: this.$i18n.t('action.duplicate'),
          operate: this.duplicateECircular,
        })
      }
      if (this.canCancel) {
        options.push({
          title: this.$i18n.t('action.cancel'),
          operate: this.openCancelModal,
        })
      }
      return options
    },
    curECircularDepartment () {
      return this.currentECircular.category.department
    },
    isTenantOrTenancyEcircular () {
      return this.isTenantEcircular || this.isTenancyEcircular
    },
    isTenantEcircular () {
      return this.currentECircular.type === E_CIRCULAR_TYPES.TENANT
    },
    isTenancyEcircular () {
      return this.currentECircular.type === E_CIRCULAR_TYPES.TENANCY
    },
    isVendorEcircular () {
      return this.currentECircular.type === E_CIRCULAR_TYPES.VENDOR
    },
    isMarketingECircular () {
      return this.curECircularDepartment && this.curECircularDepartment.shortname === 'MC'
    },
    isPmCircularSearchDisabled () {
      const noZoneOrTrade = !this.pmCircularParams.zone && !this.pmCircularParams.tradeName
      const noUserType =
        this.isTenantOrTenancyEcircular && this.selectedUserContactTypes.length === 0
      return noZoneOrTrade || noUserType
    },
    isMarketingSearchDisabled () {
      const noZoneOrTrade = !this.marketingParams.zone && !this.marketingParams.tradeName
      const noMarketingTypes = !(this.marketingParams.marketingTypes && this.marketingParams.marketingTypes.length)
      return noZoneOrTrade || noMarketingTypes
    },
  },
  async mounted () {
    this.ecircularId = this.$route.params.id
    if (!this.currentECircular.id) {
      await this.loadECircular(this.ecircularId)
    }
    if (!this.editableCheck()) return
    if (!this.isVendorEcircular) {
      const buildings = get(this.currentECircular, 'buildings', [])
      if (buildings.length) {
        const userBuildings = get(this.currentUser, 'buildings')
        const zone = get(userBuildings.find(building => building.code === buildings[0]), ['zones', 0])
        this.marketingParams.zone = zone
        this.pmCircularParams.zone = zone
      }
      if (!isEmpty(this.searchRecipientsConditions)) {
        this.marketingParams = cloneDeep(this.searchRecipientsConditions.marketingParams)
        this.pmCircularParams = cloneDeep(this.searchRecipientsConditions.pmCircularParams)
        this.companyName = this.searchRecipientsConditions.companyName
        this.syncSelectedUserContactTypesFromParams()
      } else if (
        this.isTenantEcircular &&
        Array.isArray(this.currentECircular.userType) &&
        this.currentECircular.userType.length
      ) {
        this.pmCircularParams.userContactTypes = {
          ...buildUserContactTypesFlagsFromSavedUserType(this.currentECircular.userType),
        }
        const marketingLabels = buildMarketingTypeLabelsFromSavedUserType(this.currentECircular.userType)
        if (marketingLabels.length) {
          this.marketingParams.marketingTypes = marketingLabels
        }
        this.syncSelectedUserContactTypesFromParams()
        this.initPair()
      } else {
        this.initPair()
      }
    }
  },
  beforeDestroy () {
    this.saveSearchResult([])
  },
  methods: {
    ...mapActions('ecircular', ['searchRecipients', 'loadECircular', 'updateRecipients', 'createECircular']),
    ...mapActions('user', ['reloadUser']),
    ...mapMutations('ecircular', {
      saveConditions: mutationTypes.ECIRCULAR_SAVE_SEARCH_RECIPIENT_CONDITIONS,
      saveSearchResult: mutationTypes.ECIRCULAR_SAVE_SEARCH_RECIPIENTS,
      updateAddedRecipients: mutationTypes.ECIRULAR_ADDED_RECIPIENTS,
    }),
    async duplicateECircular () {
      await this.createECircular({id: this.currentECircular.id})
    },
    /**
     * Rehydrates the user-type HKLMultiSelect from `pmCircularParams.userContactTypes` after Vuex search conditions are restored.
     * @date 2026-04-15
     * @author Alvis Yip
     */
    syncSelectedUserContactTypesFromParams () {
      const uc = this.pmCircularParams.userContactTypes || {}
      this.selectedUserContactTypes = this.userTypeOpts
        .map((opt) => opt.value)
        .filter((key) => !!uc[key])
    },
    async _searchRecipients (isMarketing = false) {
      const params = isMarketing ? this.marketingParams : this.pmCircularParams
      if (isMarketing && !(params.marketingTypes && params.marketingTypes.length)) {
        return
      }
      if (!isMarketing && this.isTenantOrTenancyEcircular && isEmpty(params.userContactTypes)) {
        return
      }
      let request
      if (this.isTenantEcircular) {
        request = {
          eCircularType: this.currentECircular.type,
          tradeName: params.tradeName,
          zone: params.zone,
          isMarketing,
          userContactTypes: params.userContactTypes,
        }
        if (isMarketing) {
          request.roleTypes = JSON.stringify(params.marketingTypes)
        }
        if (params.floorFromToPair && params.floorFromToPair.length > 0) {
          const queryFloors = []
          const floorOpts = this.floorOptions || []
          params.floorFromToPair.forEach(pair => {
            if (!pair || pair.err) return
            const fromIndex = Number.isInteger(pair.fromIndex) ? pair.fromIndex : floorOpts.indexOf(pair.from)
            const toIndex = Number.isInteger(pair.toIndex) ? pair.toIndex : floorOpts.indexOf(pair.to)
            if (fromIndex > -1 && toIndex > -1 && fromIndex <= toIndex) {
              queryFloors.push(...floorOpts.slice(fromIndex, toIndex + 1))
            }
          })
          if (queryFloors.length) {
            // preserve order, remove duplicates
            const uniqueFloors = [...new Set(queryFloors)]
            request.floors = JSON.stringify(uniqueFloors)
          }
        }
      }
      if (this.isTenancyEcircular) {
        request = {
          eCircularType: this.currentECircular.type,
          tradeName: params.tradeName,
          zone: params.zone,
        }
      }
      this.saveConditions({
        ecircularId: this.ecircularId,
        marketingParams: cloneDeep(this.marketingParams),
        pmCircularParams: cloneDeep(this.pmCircularParams),
        companyName: this.companyName,
      })
      await this.searchRecipients(request)
      this.initSelectRecipientsTree(isMarketing ? 'select-marketing-recipients' : 'select-recipients')
    },
    initSelectRecipientsTree (refId, remainingSelectableTrees) {
      const tree = this.$refs[refId]
      if (!tree) return
      if (Array.isArray(remainingSelectableTrees) && tree.syncSelectionAfterFilterPrune) {
        tree.syncSelectionAfterFilterPrune(remainingSelectableTrees)
        return
      }
      tree.initTreeStatus()
    },
    updateZone (value, paramName) {
      const zone = value || ''
      if (paramName === 'pmCircularParams' && this[paramName].zone !== zone) {
        this.initPair()
      }
      this[paramName].zone = zone
    },
    getRemovedListItems (before = [], after = []) {
      const afterSet = new Set(after)
      return (before || []).filter((item) => !afterSet.has(item))
    },
    promptRemoveFilterRecipients (onConfirm, onCancel) {
      this.$refs.filterRemoveConfirmModal.open(onConfirm, [], onCancel)
    },
    applyPrunedAddedRecipients ({ addedRecipients, searchTreeRefId, remainingSelectableTrees }) {
      this.updateAddedRecipients(addedRecipients)
      this.$nextTick(() => this.refreshSearchRecipientTreesAfterPrune(searchTreeRefId, remainingSelectableTrees))
    },
    confirmAndApplyPruneIfNeeded ({ nextAfterPrune, remainingSelectableTrees, onCancel, searchTreeRefId }) {
      if (isEqual(nextAfterPrune, this.addedRecipients)) {
        this.$nextTick(() => this.refreshSearchRecipientTreesAfterPrune(searchTreeRefId, remainingSelectableTrees))
        return
      }
      this.promptRemoveFilterRecipients(
        () => {
          this.applyPrunedAddedRecipients({
            addedRecipients: nextAfterPrune,
            searchTreeRefId,
            remainingSelectableTrees,
          })
        },
        onCancel,
      )
    },
    onUserContactTypesChange (val) {
      const searchTreeRefId = 'select-recipients'
      const snapshot = cloneDeep(this.selectedUserContactTypes || [])
      const next = Array.isArray(val) ? val : []
      const removedKeys = this.getRemovedListItems(snapshot, next)
      this.selectedUserContactTypes = next
      if (!removedKeys.length || !this.isTenantEcircular) {
        return
      }
      const nextAfterPrune = this.getPrunedRecipientsByTrRemovals({
        selectedAfterRemove: next,
        removedKeys,
      })
      const currentSearchTrees = this.getSearchRecipientTreesForRef(searchTreeRefId)
      const remainingSelectableTrees = this.getPrunedRecipientsByTrRemovals({
        selectedAfterRemove: next,
        removedKeys,
        recipientTrees: currentSearchTrees,
      })
      this.confirmAndApplyPruneIfNeeded({
        nextAfterPrune,
        remainingSelectableTrees,
        searchTreeRefId,
        onCancel: () => {
          this.selectedUserContactTypes = snapshot
        },
      })
    },
    onMarketingTypesChange (val) {
      const searchTreeRefId = 'select-marketing-recipients'
      const snapshot = cloneDeep(this.marketingParams.marketingTypes || [])
      const next = Array.isArray(val) ? val : []
      const removedLabels = this.getRemovedListItems(snapshot, next)
      this.marketingParams.marketingTypes = next
      if (!removedLabels.length) {
        return
      }
      if (!this.isTenantEcircular) {
        return
      }
      const nextAfterPrune = this.getPrunedRecipientsByMarketingRemovals({
        selectedAfterRemove: next,
        removedLabels,
      })
      const currentSearchTrees = this.getSearchRecipientTreesForRef(searchTreeRefId)
      const remainingSelectableTrees = this.getPrunedRecipientsByMarketingRemovals({
        selectedAfterRemove: next,
        removedLabels,
        recipientTrees: currentSearchTrees,
      })
      this.confirmAndApplyPruneIfNeeded({
        nextAfterPrune,
        remainingSelectableTrees,
        searchTreeRefId,
        onCancel: () => {
          this.marketingParams.marketingTypes = snapshot
        },
      })
    },
    resolveTrCodeFromOptionKey (key) {
      if (key === 'isCircular') return E_CIRCULAR_USER_TYPE_CODES.isCircular
      if (key === 'isFireMarshal') return E_CIRCULAR_USER_TYPE_CODES.isFireMarshal
      return null
    },
    hasTrFilterByKey (selected = [], key) {
      return (selected || []).includes(key)
    },
    getPrunedRecipientsByTrSelection ({ selectedAfterRemove, removedKey, removedCode, recipientTrees = this.addedRecipients }) {
      const otherTrStillSelected =
        removedKey === 'isCircular'
          ? this.hasTrFilterByKey(selectedAfterRemove, 'isFireMarshal')
          : this.hasTrFilterByKey(selectedAfterRemove, 'isCircular')
      return otherTrStillSelected
        ? pruneAddedRecipientsExclusiveTrCode(recipientTrees, removedCode)
        : pruneAddedRecipientsAnyLeafWithTrCode(recipientTrees, removedCode)
    },
    getPrunedRecipientsByTrRemovals ({ selectedAfterRemove, removedKeys = [], recipientTrees = this.addedRecipients }) {
      return (removedKeys || []).reduce((trees, removedKey) => {
        const removedCode = this.resolveTrCodeFromOptionKey(removedKey)
        if (!removedCode) return trees
        const otherTrStillSelected =
          removedKey === 'isCircular'
            ? this.hasTrFilterByKey(selectedAfterRemove, 'isFireMarshal')
            : this.hasTrFilterByKey(selectedAfterRemove, 'isCircular')
        return otherTrStillSelected
          ? pruneAddedRecipientsExclusiveTrCode(trees, removedCode)
          : pruneAddedRecipientsAnyLeafWithTrCode(trees, removedCode)
      }, cloneDeep(recipientTrees || []))
    },
    getPrunedRecipientsByMarketingSelection ({ selectedAfterRemove, removedLabel, recipientTrees = this.addedRecipients }) {
      const hasActiveMarketingTypes = selectedAfterRemove && selectedAfterRemove.length
      return hasActiveMarketingTypes
        ? pruneAddedRecipientsExclusiveMarketingRole(recipientTrees, removedLabel)
        : pruneAddedRecipientsAnyLeafWithMarketingRole(recipientTrees)
    },
    getPrunedRecipientsByMarketingRemovals ({ selectedAfterRemove, removedLabels = [], recipientTrees = this.addedRecipients }) {
      return (removedLabels || []).reduce((trees, removedLabel) => {
        const hasActiveMarketingTypes = selectedAfterRemove && selectedAfterRemove.length
        return hasActiveMarketingTypes
          ? pruneAddedRecipientsExclusiveMarketingRole(trees, removedLabel)
          : pruneAddedRecipientsAnyLeafWithMarketingRole(trees)
      }, cloneDeep(recipientTrees || []))
    },
    getSearchRecipientTreesForRef (refId) {
      return cloneDeep(get(this.$refs, [refId, 'treeData']) || [])
    },
    /**
     * Frontend-only: when removing one TR filter tag, if the *other* TR tag remains → drop **exclusive** matches only.
     * If the other TR tag is already gone (user deleted both filters by removing the last tag) → drop any leaf whose
     * `userContactTypes` still includes that code (eCir-only, fire-only, or eCir+fire).
     */
    /** Resync ADD trees with Vuex after prune (DEL tree updates via its own addedRecipients watch). */
    refreshSearchRecipientTreesAfterPrune (refId, remainingSelectableTrees) {
      if (refId) {
        const tree = this.$refs[refId]
        if (tree && Array.isArray(remainingSelectableTrees) && tree.syncSelectionAfterFilterPrune) {
          tree.syncSelectionAfterFilterPrune(remainingSelectableTrees)
        } else {
          tree && tree.initTreeStatus()
        }
        return
      }
      const sel = this.$refs['select-recipients']
      sel && sel.initTreeStatus()
      if (this.isMarketingECircular) {
        const m = this.$refs['select-marketing-recipients']
        m && m.initTreeStatus()
      }
    },
    updateTradeName (val, paramName) {
      this[paramName].tradeName = val.currentTarget.value
    },
    updateCompanyName (val) {
      this.companyName = val.currentTarget.value
    },
    addToAddedRecipients () {
      this.$refs['select-recipients']._addToAddedRecipients()
      this.isMarketingECircular && this.$refs['select-marketing-recipients']._addToAddedRecipients()
      this.initSelectRecipientsTree('delete-recipients')
    },
    syncSearchRecipientConditionsToStore () {
      if (this.isVendorEcircular) {
        const vendorSearch = this.$refs['vendor-recipients-search']
        vendorSearch && vendorSearch.syncVendorSearchConditionsToStore()
        return
      }
      this.saveConditions({
        ecircularId: this.ecircularId,
        marketingParams: cloneDeep(this.marketingParams),
        pmCircularParams: cloneDeep(this.pmCircularParams),
        companyName: this.companyName,
      })
    },
    async persistRecipients ({ showNotification = false, openPreview = false } = {}) {
      this.syncSearchRecipientConditionsToStore()
      await this.updateRecipients(showNotification)
      if (openPreview) {
        this.$refs.previewECircular.open()
      }
    },
    async save () {
      await this.persistRecipients({ showNotification: true })
    },
    async preview () {
      await this.persistRecipients({ openPreview: true })
    },
    updateFloorFromToPair (val, index, flag) {
      this.pmCircularParams.floorFromToPair[index][flag] = val || ''
      const toValue = this.pmCircularParams.floorFromToPair[index]['to']
      const fromValue = this.pmCircularParams.floorFromToPair[index]['from']
      const toIndex = findIndex(this.floorOptions, floor => {
        return toValue === floor
      })
      const fromIndex = findIndex(this.floorOptions, floor => {
        return fromValue === floor
      })
      if (fromIndex === -1 || toIndex === -1) {
        this.pmCircularParams.floorFromToPair[index]['err'] = false
      } else {
        this.pmCircularParams.floorFromToPair[index]['err'] = fromIndex > toIndex
      }
      this.pmCircularParams.floorFromToPair[index].fromIndex = fromIndex
      this.pmCircularParams.floorFromToPair[index].toIndex = toIndex
      this.$forceUpdate()
    },
    addOnePair () {
      this.pmCircularParams.floorFromToPair.push({
        from: '',
        to: '',
        err: false,
      })
    },
    initPair () {
      this.pmCircularParams.floorFromToPair = [{
        from: '',
        to: '',
        err: false,
      }]
    },

    deleteOnePair (index) {
      if (this.pmCircularParams.floorFromToPair.length === 1) {
        this.initPair()
      } else {
        this.pmCircularParams.floorFromToPair.splice(index, 1)
      }
    },
    openCancelModal () {
      this.$refs['ecircular-cancel-modal'].open()
    },
    editableCheck () {
      if (!E_CIRCULAR_EDITABLE_STATUS[this.currentECircular.status]) {
        navigation.replacePage('eCircularDetail', this.currentECircular.id)
        return false
      }
      return true
    },
  },
}
</script>

<style lang="scss">
@import '~styles/multi-value';
.marketing-type {
  ::v-deep .hkl-select .ant-select-selection {
    overflow-y: auto;
  }
}
</style>
<style lang='scss' scoped>
.recipients {
  height: 100%;
  .tms-header {
    padding-left: 15px;
    padding-right: 15px;
  }
  .recipients-content {
    max-width: 1440px;
    margin: 20px auto;
    height: calc(100% - 188px);
    display: flex;
    flex: 1;
    flex-direction: row;
    .floorFromToPair {
      display: inline-flex;
      width: 180px;
      align-items: center;
      margin-bottom: 10px;
      margin-right: 5px;
      & ::v-deep .hkl-select {
        min-width: 80px;
      }
    }
    .floorFromToPair + .floorFromToPair {
      margin-left: 15px;
    }

    .error-highlight {
      input,
      ::v-deep .hkl-select .ant-select-selection {
        background: #f9ecea!important;
      }
    }
    label {
      font-weight: bold;
      color: #043251;
      height: 25px !important;
      margin-top: 0 !important;
    }
    .search-area, .search-area_marketing, .selected-recipients {
      background: white;
      min-height: 600px;
      box-shadow: 0 2px 4px 0 rgba(212, 212, 212, 0.5);
      padding: 10px;
      p.title {
        font-family: Verdana;
        font-size: 16px;
        font-weight: bold;
        font-style: normal;
        color: #0c3a58;
      }
    }
    .search-area, .search-area_marketing {
      height: fit-content;
    }
    .search-arrow-area {
      width: calc(50% + 40px);
      display: flex;
      flex-direction: row;
      .tabs-container-wrapper {
        flex: 1;
      }
    }
    .selected-recipients {
      width: calc(50% - 40px);
      height: max-content;
    }
    .search-condition {
      .zone, .trade-name, .company-name, .user-type-field {
        margin-bottom: 10px;
      }
      .user-type-field {
        ::v-deep .hkl-select .ant-select-search__field {
          text-overflow: clip;
        }
      }
      .trade-name, .company-name {
        .search-input {
          display: flex;
          width: 100%;
          .input-field {
            flex: 1;
            padding-left: 8px;
            margin-right: 30px;
            color: #0C3A58;
            line-height: 1;
          }
        }
      }
    }
    .selected-recipients {
      .mount-recipients {
        color: #10416c;
        margin-bottom: 10px;
        .item {
          margin-right: 10px;
        }
        .tip {
          margin-right: 5px;
          border-radius: 10px;
          background-color: #083e62;
          color: #fff;
          font-weight: bold;
          padding: 2px 8px;
        }
      }
    }
    .fa-plus.fa {
      margin-right: 10px;
      font-size: 18px;
      cursor: pointer;
    }
    .fa-times {
      margin-left: 5px;
      font-size: 18px;
      cursor: pointer;
    }
    .arrow-area {
      width: 80px;
      align-self: center;
      text-align: center;
    }
  }
}
</style>
